<template>
    <v-form ref="form" class="pb-4">
        <v-row dense class="pb-6 px-2">
            <v-divider class="mb-4" />
            <v-data-table
                :items="accommodations"
                :headers="useHeaders"
                hide-default-footer
                disable-sort
                density="compact"
                class="table-sm"
            >
                <template v-slot:item.dayNumberFrom="{item}">
                    <div class="my-3">
                        <v-autocomplete
                            v-model="item.dayNumberFrom"
                            :items="useDays"
                            :label="$t('day_number')"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            width="150px"
                            data-cy="day-from"
                            clearable
                        />
                    </div>
                </template>
                <template v-slot:item.dayNumberTo="{item}">
                    <div class="my-3">
                        <v-autocomplete
                            v-model="item.dayNumberTo"
                            :items="useDays"
                            :label="$t('day_number')"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            width="150px"
                            data-cy="day-to"
                            clearable
                        />
                    </div>
                </template>
                <template v-slot:item.cityId="{item}">
                    <div class="my-3">
                        <city-autocomplete
                            v-model="item.cityId"
                            class="rounded"
                            :show-icon="false"
                            dense
                            show-details
                            @update:model-value="onCityUpdate(item)"
                        />
                    </div>
                </template>
                <template v-slot:item.category="{item, column}">
                    <div v-if="item.categoryToHotel[column?.index]" class="my-3">
                        <hotel-autocomplete
                            v-model="item.categoryToHotel[column?.index].hotelId"
                            :city-id="item.cityId?.id || null"
                            :disabled="!item.cityId?.id"
                            :show-icon="false"
                            :base-label="$t('productType.accommodation')"
                            :content-provider-id="1"
                            show-details
                            class="rounded"
                            dense
                            :is-sort-by-name="true"
                        />
                    </div>
                </template>
                <template v-slot:item.action="{index}">
                    <v-btn
                        variant="text"
                        color="primary"
                        class="text-caption"
                        data-cy="delete"
                        density="compact"
                        @click="remove(index)"
                    >
                        {{ $t('delete') }}
                    </v-btn>
                </template>
            </v-data-table>
            <v-col cols="12">
                <div class="full-width d-flex align-center">
                    <v-btn variant="flat" size="small" color="primary" @click="addAccommodation">
                        {{ $t('add') }}
                    </v-btn>
                </div>
            </v-col>
        </v-row>
        <v-row dense class="px-2">
            <v-btn
                color="primary"
                class="mr-4"
                data-cy="save"
                :disabled="isLoading || !accommodations.length"
                @click="save"
            >
                {{ $t('save') }}
            </v-btn>
            <v-btn color="white" data-cy="cancel" :disabled="isLoading" @click="cancel">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>
<script setup lang="ts">
    import CityAutocomplete from '~/components/search/forms/CityAutocomplete.vue'
    import HotelAutocomplete from '~/components/search/forms/HotelAutocomplete.vue'
    import {clone} from '~/utils/helpers'

    const isLoading = ref<boolean>(false)
    const accommodations = ref([])
    const defaultData = ref([])

    const app = useNuxtApp()
    const router = useRouter()
    const {t} = useI18n()

    onMounted(async () => {
        if (!useCatalogue.value?.length) await supplierStore.loadCatalogue()
        if (supplierStore.contract.id) {
            await loadData()
        }
    })

    watch(
        () => useCatalogue.value.length,
        async () => {
            if (supplierStore.contract.id && accommodations.value.length) {
                accommodations.value = await prepareData()
            }
        }
    )

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const res = await app.$api.ownTourContractsAccommodation.get(supplierStore.contract.id)
            defaultData.value = res.accommodations
            accommodations.value = await prepareData()
        } catch (e) {
            accommodations.value = []
        } finally {
            isLoading.value = false
        }
    }

    async function prepareData() {
        let data = clone(defaultData.value)
        const processedData = await Promise.all(
            data.map(async accommodation => {
                let cityId = null
                let categoryToHotel = []
                if (accommodation?.cityId) {
                    cityId = await getLocation(accommodation?.cityId)
                }

                if (useCatalogue.value?.length) {
                    categoryToHotel = await Promise.all(
                        useCatalogue.value.map(async category => {
                            const categoryItem = accommodation.categoryToHotel.find(
                                cat => cat.categoryId === category.id
                            )
                            if (categoryItem && categoryItem?.hotelId) {
                                let hotelId = await getHotel(categoryItem.hotelId)
                                return {
                                    categoryId: categoryItem.categoryId,
                                    hotelId: {
                                        ...hotelId,
                                        countryName: cityId.countryName,
                                        cityName: cityId.name,
                                        parentName: cityId.parentName,
                                        type: 'hotel',
                                    },
                                }
                            } else {
                                return {categoryId: category.id, hotelId: null}
                            }
                        })
                    )
                }

                return {
                    ...accommodation,
                    cityId,
                    categoryToHotel,
                }
            })
        )
        return processedData
    }

    async function getHotel(hotelId) {
        const {
            hotels: [hotel],
        } = await app.$api.hotels.getFull({
            hotelId,
            contentProviderId: 1,
            limit: 1,
        })
        return hotel
    }

    async function getLocation(cityId) {
        try {
            const {
                cities: [city],
            } = await app.$api.locations.get({id: cityId, limit: 1})
            return {
                name: city.name,
                id: city.id,
                countryName: city.countryName,
                parentName: city.parentName,
            }
        } catch (e) {
            return null
        }
    }

    const cancel = (): void => {
        router.push({name: 'supplierProducts', query: {productTab: supplierStore.contract.serviceType}})
    }

    const save = async (): Promise<void> => {
        let data = clone(accommodations.value)
        data = data.map(({dayNumberFrom, dayNumberTo, cityId, categoryToHotel}) => ({
            dayNumberFrom,
            dayNumberTo,
            cityId: cityId?.id || null,
            categoryToHotel: categoryToHotel
                .map(category => ({
                    ...category,
                    hotelId: category?.hotelId?.id || null,
                }))
                .filter(({hotelId}) => hotelId),
        }))

        try {
            isLoading.value = true
            await app.$api.ownTourContractsAccommodation.put(supplierStore.contract.id, {
                accommodations: data,
            })
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const useHeaders = computed(() => {
        let headers = [
            {title: '', value: 'dayNumberFrom', minWidth: '190px', width: '190px'},
            {title: '', value: 'dayNumberTo', minWidth: '190px', width: '190px'},
            {title: t('destination'), value: 'cityId', minWidth: '250px', width: '25%'},
        ]
        if (useCatalogue.value?.length) {
            useCatalogue.value.forEach(({name, id}, index) => {
                headers.push({title: name, value: 'category', id, index, minWidth: '250px', width: '25%'})
            })
        }
        headers.push({title: '', value: 'action', align: 'end'})
        return headers
    })

    const addAccommodation = () => {
        let defaultItem = {
            dayNumberFrom: null,
            dayNumberTo: null,
            cityId: null,
            categoryToHotel: [],
        }
        useCatalogue.value.forEach(({id}) => {
            defaultItem.categoryToHotel.push({categoryId: id, hotelId: null})
        })
        accommodations.value.push(defaultItem)
    }

    const useDays = computed(() => {
        return Array.from({length: supplierStore.fullContract.duration}, (_, index) => index + 1)
    })

    const remove = index => {
        accommodations.value.splice(index, 1)
    }

    const onCityUpdate = item => {
        item.categoryToHotel = item.categoryToHotel.map(category => ({
            ...category,
            hotelId: null,
        }))
    }
</script>
<style scoped lang="scss">
    .rounded {
        :deep(.v-field) {
            border-radius: 4px !important;
        }
    }

    .table-sm {
        :deep(td) {
            padding: 0 6px !important;
        }
        :deep(th) {
            padding: 0 6px !important;
        }
    }
</style>
